@if (ready()) {
  <div [formGroup]="form" class="flex flex-col gap-2xl py-lg">
    @if (serverErrorMessages().length) {
      <ul
        id="reflection_server_errors"
        class="flex flex-col gap-xs rounded-lg border border-destructive-300 bg-destructive-50 p-md text-sm text-destructive-700">
        @for (message of serverErrorMessages(); track message) {
          <li>{{ message }}</li>
        }
      </ul>
    }

    <!-- ── When ── -->
    <section class="flex flex-col gap-md">
      <h3 class="text-md font-semibold text-neutral-900">
        {{ 'reflection_editor_when' | transloco }}
      </h3>
      @if (eventOptions().length) {
        <div>
          <lib-select
            inputFormControlName="event"
            id="reflection_event"
            name="event"
            [label]="'reflection_event_label' | transloco"
            [placeholder]="'reflection_event_placeholder' | transloco"
            [required]="true"
            [options]="eventOptions()"
            [selectedKeyName]="'label'"
            [selectedKeyValue]="'value'" />
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="event" />
        </div>
      }
    </section>

    <!-- ── Do ── -->
    <section class="flex flex-col gap-md">
      <h3 class="text-md font-semibold text-neutral-900">
        {{ 'reflection_editor_do' | transloco }}
      </h3>
      @if (actionOptions().length) {
        <div>
          <lib-select
            inputFormControlName="hook_key"
            id="reflection_action"
            name="hook_key"
            [label]="'reflection_action_label' | transloco"
            [placeholder]="'reflection_action_placeholder' | transloco"
            [required]="true"
            [options]="actionOptions()"
            [selectedKeyName]="'label'"
            [selectedKeyValue]="'value'"
            [hasSearch]="true"
            [searchPlaceholder]="'type_to_search_placeholder' | transloco" />
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="hook_key" />
        </div>
      }
      @if (actionDescription()) {
        <p class="text-xs text-neutral-500">{{ actionDescription() }}</p>
      }
    </section>

    <!-- ── Configure ── -->
    @if (paramRows().length) {
      <section class="flex flex-col gap-md">
        <h3 class="text-md font-semibold text-neutral-900">
          {{ 'reflection_editor_configure' | transloco }}
        </h3>
        @for (row of paramRows(); track row.param.key) {
          <lib-reflection-param-input
            [param]="row.param"
            [group]="configGroup()"
            [fields]="fields()"
            [placeholders]="placeholderOptions()"
            [scope]="scope()"
            [hasSecret]="row.hasSecret"
            [userLabels]="userLabels()" />
        }
      </section>
    }

    <!-- ── Only if ── -->
    <section class="flex flex-col gap-md">
      <h3 class="text-md font-semibold text-neutral-900">
        {{ 'reflection_editor_only_if' | transloco }}
      </h3>
      <p class="text-xs text-neutral-500">
        {{ 'reflection_editor_only_if_hint' | transloco }}
      </p>
      <lib-reflection-conditions
        [group]="conditionsGroup()"
        [fields]="fields()"
        [operators]="operators()"
        [userLabels]="userLabels()" />
    </section>

    <!-- ── Options ── -->
    <section class="flex flex-col gap-md">
      <h3 class="text-md font-semibold text-neutral-900">
        {{ 'reflection_editor_options' | transloco }}
      </h3>
      @if (isFormScope()) {
        <div id="reflection_watch_fields" class="flex flex-col gap-sm">
          @if (fieldOptions().length) {
            <lib-multi-select
              inputFormControlName="watch_fields"
              id="reflection_watch_fields_select"
              name="watch_fields"
              [label]="'reflection_watch_fields_label' | transloco"
              [options]="fieldOptions()"
              [selectedKeyName]="'label'"
              [selectedKeyValue]="'value'" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="watch_fields" />
          }
          <p class="text-xs text-neutral-500">
            {{ 'reflection_watch_fields_hint' | transloco }}
          </p>
          <div
            id="reflection_derived_watch"
            class="flex flex-wrap items-center gap-xs text-xs text-neutral-700">
            <span class="font-medium">
              {{ 'reflection_watch_fields_derived_label' | transloco }}
            </span>
            @for (name of derivedWatchLabels(); track name) {
              <span class="rounded-md bg-neutral-100 px-sm py-xs">
                {{ name }}
              </span>
            } @empty {
              <span class="text-neutral-500">
                {{ 'reflection_watch_fields_none' | transloco }}
              </span>
            }
          </div>
        </div>
      } @else {
        <div id="reflection_skip_when_returned" class="flex flex-col gap-xs">
          <lib-toggle
            id="reflection_skip_when_returned_toggle"
            [formGroup]="form"
            inputFormControlName="skip_when_returned"
            [options]="skipToggleOption()" />
          <p class="text-xs text-neutral-500">
            {{ 'reflection_skip_when_returned_hint' | transloco }}
          </p>
        </div>
      }
    </section>

    <div class="flex justify-end gap-md">
      <lib-button
        id="reflection_cancel"
        [label]="'reflection_editor_cancel' | transloco"
        [btnClass]="'py-sm px-lg text-sm rounded-md text-neutral-700 bg-white font-medium border border-neutral-300'"
        (click)="cancel()" />
      <lib-button
        id="reflection_save"
        [disabled]="isSaving()"
        [label]="'reflection_editor_save' | transloco"
        [btnClass]="'py-sm px-lg text-sm rounded-md text-white bg-primary-500 font-medium border-none'"
        (click)="save()" />
    </div>
  </div>
}
